import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilterBar from "@/components/feature/FilterBar";
import MovieCard from "@/components/feature/MovieCard";
import { getCategoryById, getMoviesByCategory, filterMovies, type EraKey, type SortKey } from "@/utils/movie";

const PAGE_SIZE = 6;

export default function Category() {
  const { categoryId = "" } = useParams();
  const category = getCategoryById(categoryId);

  const [region, setRegion] = useState("全部");
  const [era, setEra] = useState<EraKey>("all");
  const [sort, setSort] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);
  const [mobileCount, setMobileCount] = useState(PAGE_SIZE);

  useEffect(() => {
    setRegion("全部");
    setEra("all");
    setSort("latest");
    setPage(1);
    setMobileCount(PAGE_SIZE);
  }, [categoryId]);

  const filtered = useMemo(() => {
    if (!category) return [];
    return filterMovies(getMoviesByCategory(category.id), { region, era, sort });
  }, [category, region, era, sort]);

  useEffect(() => {
    setPage(1);
    setMobileCount(PAGE_SIZE);
  }, [region, era, sort]);

  if (!category) {
    return (
      <SiteLayout>
        <div className="mx-auto w-full max-w-3xl px-4 md:px-6 py-24 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-3">未找到该分类</h1>
          <p className="text-sm text-foreground-500 mb-6">你访问的分类可能已被移除，试试浏览其他分类。</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-foreground-950 hover:bg-primary-600 transition-colors"
          >
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const desktopItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const mobileItems = filtered.slice(0, mobileCount);

  const resetFilters = () => {
    setRegion("全部");
    setEra("all");
    setSort("latest");
  };

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-6 pt-6 md:pt-8">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "影片分类", to: "/search" },
            { label: category.name },
          ]}
        />

        <header className="mt-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-start gap-4">
            <span className="w-14 h-14 shrink-0 flex items-center justify-center rounded-lg bg-primary-500/15 text-primary-300">
              <i className={`${category.icon} text-3xl`} />
            </span>
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                {category.name}
              </h1>
              <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-foreground-500">
                {category.description}
              </p>
            </div>
          </div>
          <span className="shrink-0 rounded-full bg-secondary-500/20 px-4 py-1.5 text-sm text-secondary-300">
            共 {filtered.length} 部影片
          </span>
        </header>

        <div className="mt-6">
          <FilterBar
            region={region}
            onRegionChange={setRegion}
            era={era}
            onEraChange={setEra}
            sort={sort}
            onSortChange={setSort}
          />
        </div>

        {filtered.length === 0 ? (
          <div className="mt-10 flex flex-col items-center rounded-lg border border-background-200/70 bg-background-100 py-16 text-center">
            <span className="w-14 h-14 flex items-center justify-center rounded-full bg-background-200/70 text-foreground-500 mb-4">
              <i className="ri-inbox-line text-2xl" />
            </span>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-2">没有符合条件的影片</h2>
            <p className="text-sm text-foreground-500 mb-6">换个地区或年代筛选条件试试看。</p>
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-foreground-950 hover:bg-accent-600 transition-colors"
            >
              <i className="ri-refresh-line" />
              重置筛选
            </button>
          </div>
        ) : (
          <>
            <div className="mt-6 hidden md:grid grid-cols-3 lg:grid-cols-6 gap-3 md:gap-5">
              {desktopItems.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
            <div className="mt-6 grid md:hidden grid-cols-2 gap-3">
              {mobileItems.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>

            <div className="mt-8 hidden md:flex items-center justify-center gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                className="flex items-center gap-1 rounded-md border border-background-200/70 bg-background-100 px-3 py-2 text-sm text-foreground-600 hover:text-foreground-950 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-s-line" />
                上一页
              </button>
              {Array.from({ length: totalPages }, (_, index) => index + 1).map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setPage(item)}
                  className={`w-9 h-9 rounded-md text-sm transition-colors ${
                    item === page
                      ? "bg-primary-500 text-foreground-950 font-medium"
                      : "border border-background-200/70 bg-background-100 text-foreground-600 hover:text-foreground-950"
                  }`}
                >
                  {item}
                </button>
              ))}
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                className="flex items-center gap-1 rounded-md border border-background-200/70 bg-background-100 px-3 py-2 text-sm text-foreground-600 hover:text-foreground-950 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
              >
                下一页
                <i className="ri-arrow-right-s-line" />
              </button>
            </div>

            {mobileCount < filtered.length && (
              <div className="mt-8 md:hidden">
                <button
                  type="button"
                  onClick={() => setMobileCount((prev) => prev + PAGE_SIZE)}
                  className="w-full rounded-md border border-background-200/70 bg-background-100 py-3 text-sm font-medium text-foreground-800 hover:border-accent-500 hover:text-accent-500 transition-colors"
                >
                  加载更多影片
                </button>
              </div>
            )}
          </>
        )}
      </div>
      <div className="h-12 md:h-16" />
    </SiteLayout>
  );
}